import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api";
import { hideLoader, showLoader } from "./loaderSlice";

export const getConsultationRoomList = createAsyncThunk(
  "getConsultationRoomList",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        "/getall/clinic/facilities?page=1&itemSize=100"
      );

      if (res.status === 200) {
        const consultationRooms = res.data.data.filter(
          (p) => p.type === "ConsultationRoom"
        );
        thunkAPI.dispatch(hideLoader());
        return consultationRooms;
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch consultation rooms");
    }
  }
);

export const createConsultationRoom = createAsyncThunk(
  "createConsultationRoom",
  async ({ name, status = true }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/create/clinic/facilites?type=ConsultationRoom&name=${name}&status=${status}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create consultation room");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const updateConsultationRoom = createAsyncThunk(
  "updateConsultationRoom",
  async ({ facilityId, name, status = true }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/update/clinic/facilites/${facilityId}?type=ConsultationRoom&name=${name}&status=${status}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update consultation room");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const deleteConsultationRoom = createAsyncThunk(
  "deleteConsultationRoom",
  async ({ facilityId }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/delete/clinic/facilites/${facilityId}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return facilityId; // Return ID to remove from list
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete consultation room");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

const consultationRoomSlice = createSlice({
  name: "consultationRoom",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getConsultationRoomList.fulfilled, (state, action) => {
        state.list = action.payload ?? [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createConsultationRoom.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateConsultationRoom.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.list.findIndex(
          (p) => p.facilityId === updated.facilityId
        );
        if (index !== -1) {
          state.list[index] = updated;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteConsultationRoom.fulfilled, (state, action) => {
        const id = action.payload;
        state.list = state.list.filter((p) => p.facilityId !== id);
        state.loading = false;
        state.error = null;
      })
      .addCase(getConsultationRoomList.pending, (state) => {
        state.loading = true;
      })
      .addCase(createConsultationRoom.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateConsultationRoom.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteConsultationRoom.pending, (state) => {
        state.loading = true;
      })
      .addCase(getConsultationRoomList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching consultation rooms.";
      });
  },
});

export default consultationRoomSlice.reducer;
